import React, { useEffect, useState } from "react";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import "../styles/SellerApprove.css";

/* ------------------- GraphQL ------------------- */
const GET_SELLERS_QUERY = gql`
  query GetSellers {
    getSellers {
      customId
      firebaseUid
      name
      companyName
      gstNumber
      bankIFCnumber
      bankAccountNumber
      bankName
      authorized
      email
      phoneNumber
      status
      companyPan
      panCardUrl
      gstCertUrl
      bankPassbookUrl
      createdAt
      basicInfoVerified
      companyDetailsVerified
      bankDetailsVerified
      shiprocketDetailsVerified
      shiprocketPickupId
      shiprocketSyncSuccess
      shiprocketSyncCompletedAt
      shiprocketVerificationStatus
      shiprocketSyncAttempts
      shiprocketSyncError
      pickupAddress
      pickupCity
      pickupState
      pickupPincode
      pickupHouseNo
      shiprocketNickname
      razorpayContactId
      razorpayFundAccountId
      razorpayValidationId
      razorpayVerificationPaymentId
      razorpayVerified
      razorpayVerifiedAt
    }
  }
`;

const UPDATE_SELLER_STATUS_MUTATION = gql`
  mutation ChangeSellerStatus($customId: ID!, $status: SellerStatus!, $reason: String) {
    changeSellerStatus(customId: $customId, status: $status, reason: $reason) {
      customId
      status
    }
  }
`;

const VERIFY_PROFILE_SECTION_MUTATION = gql`
  mutation VerifyProfileSection($customId: String!, $section: String!, $verified: Boolean!, $remarks: String) {
    verifyProfileSection(customId: $customId, section: $section, verified: $verified, remarks: $remarks) {
      customId
      basicInfoVerified
      companyDetailsVerified
      bankDetailsVerified
      shiprocketDetailsVerified
    }
  }
`;

const DELETE_SELLER_MUTATION = gql`
  mutation DeleteSeller($customId: ID!) {
    deleteSeller(customId: $customId) {
      customId
    }
  }
`;

const VERIFY_SHIPROCKET_MUTATION = gql`
  mutation VerifyShiprocketSeller($customId: ID!) {
    verifyShiprocketSeller(customId: $customId) {
      customId
      shiprocketVerificationStatus
      shiprocketVerifiedAt
    }
  }
`;
// Shiprocket Status Badge Component with Verify Button (without address)
const ShiprocketStatusBadge = ({ seller, onVerify, isActionLoading }) => {
  if (!seller) return null;

  // Check if Shiprocket fields are missing
  const missingShiprocketFields = [
    !seller.pickupAddress && 'Pickup Address',
    !seller.pickupCity && 'City',
    !seller.pickupState && 'State',
    !seller.pickupPincode && 'Pincode',
  ].filter(Boolean);

  if (seller.status === 'approved' && missingShiprocketFields.length > 0) {
    return (
      <div className="shiprocket-status warning">
        <span className="status-icon">⚠️</span>
        <span className="status-text">Setup Required</span>
        <small className="missing-fields">Missing: {missingShiprocketFields.join(', ')}</small>
      </div>
    );
  }

  if (seller.shiprocketSyncSuccess) {
    // Show verify button only if not already verified
    const showVerifyButton = seller.shiprocketVerificationStatus !== 'verified';
    
    return (
      <div className="shiprocket-status success">
        <div className="status-header">
          <span className="status-icon">✅</span>
          <span className="status-text">
            {seller.shiprocketVerificationStatus === 'verified' ? 'Verified' : 'Pending Verification'}
          </span>
        </div>
        {seller.shiprocketPickupId && (
          <small className="pickup-id">ID: {seller.shiprocketPickupId.substring(0, 8)}...</small>
        )}
        {seller.shiprocketSyncCompletedAt && (
          <small className="sync-date">
            {new Date(seller.shiprocketSyncCompletedAt).toLocaleDateString()}
          </small>
        )}
        {showVerifyButton && onVerify && (
          <button
            className="verify-shiprocket-btn"
            onClick={() => onVerify(seller.customId)}
            disabled={isActionLoading}
            title="Verify Shiprocket account"
          >
            {isActionLoading ? '⏳' : '✅'} Verify
          </button>
        )}
        {seller.shiprocketVerifiedAt && (
          <small className="verified-date">
            Verified: {new Date(seller.shiprocketVerifiedAt).toLocaleDateString()}
          </small>
        )}
      </div>
    );
  }

  if (seller.shiprocketSyncAttempts > 0) {
    return (
      <div className="shiprocket-status failed">
        <span className="status-icon">❌</span>
        <span className="status-text">Sync Failed</span>
        {seller.shiprocketSyncError && (
          <small className="error-message" title={seller.shiprocketSyncError.message}>
            Error: {seller.shiprocketSyncError.message.substring(0, 30)}...
          </small>
        )}
        <small className="attempts">Attempts: {seller.shiprocketSyncAttempts}</small>
      </div>
    );
  }

  if (seller.status === 'approved') {
    return (
      <div className="shiprocket-status pending">
        <span className="status-icon">⏳</span>
        <span className="status-text">Not Synced</span>
      </div>
    );
  }

  return null;
};

// Tab component
const Tab = ({ active, label, count, onClick }) => (
  <button
    className={`tab ${active ? "active" : ""}`}
    onClick={onClick}
  >
    {label} {count > 0 && <span className="tab-count">{count}</span>}
  </button>
);

// Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage }) => {
  const pageNumbers = [];
  const maxPagesToShow = 5;

  let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
  let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

  if (endPage - startPage + 1 < maxPagesToShow) {
    startPage = Math.max(1, endPage - maxPagesToShow + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="seller-pagination-container">
      <div className="seller-pagination-info">
        Showing <strong>{startItem}-{endItem}</strong> of <strong>{totalItems}</strong> sellers
      </div>

      <div className="seller-pagination-controls">
        <button
          className="seller-pagination-btn first"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          title="First page"
        >
          ««
        </button>

        <button
          className="seller-pagination-btn prev"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          title="Previous page"
        >
          «
        </button>

        {startPage > 1 && (
          <>
            <button
              className="seller-pagination-btn"
              onClick={() => onPageChange(1)}
            >
              1
            </button>
            {startPage > 2 && <span className="seller-pagination-ellipsis">...</span>}
          </>
        )}

        {pageNumbers.map((number) => (
          <button
            key={number}
            className={`seller-pagination-btn ${currentPage === number ? 'active' : ''}`}
            onClick={() => onPageChange(number)}
          >
            {number}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="seller-pagination-ellipsis">...</span>}
            <button
              className="seller-pagination-btn"
              onClick={() => onPageChange(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}

        <button
          className="seller-pagination-btn next"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          title="Next page"
        >
          »
        </button>

        <button
          className="seller-pagination-btn last"
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          title="Last page"
        >
          »»
        </button>
      </div>

      <div className="seller-items-per-page">
        <span className="seller-items-label">Show:</span>
        <select className="seller-items-select">
          <option value="10">10</option>
          <option value="20">20</option>
          <option value="50">50</option>
          <option value="100">100</option>
        </select>
      </div>
    </div>
  );
};

// Address Component - Builds pickup address from individual fields
const AddressCell = ({ seller }) => {
  const [expanded, setExpanded] = useState(false);
  
  // Build complete pickup address from individual fields
  const getFullPickupAddress = () => {
    const parts = [];
    if (seller.pickupHouseNo && seller.pickupHouseNo.trim()) 
      parts.push(seller.pickupHouseNo.trim());
    if (seller.pickupAddress && seller.pickupAddress.trim()) 
      parts.push(seller.pickupAddress.trim());
    if (seller.pickupCity && seller.pickupCity.trim()) 
      parts.push(seller.pickupCity.trim());
    if (seller.pickupState && seller.pickupState.trim()) 
      parts.push(seller.pickupState.trim());
    if (seller.pickupPincode && seller.pickupPincode.trim()) 
      parts.push(seller.pickupPincode.trim());
    
    return parts.length > 0 ? parts.join(', ') : '-';
  };
  
  const address = getFullPickupAddress();

  if (!address || address === "-") {
    return <div className="address-cell">-</div>;
  }

  const maxLength = 50;
  const isLong = address.length > maxLength;

  return (
    <div className="address-cell">
      <div className="address-content">
        {expanded ? address : (isLong ? `${address.substring(0, maxLength)}...` : address)}
      </div>
      {isLong && (
        <button
          className="read-more-btn"
          onClick={() => setExpanded(!expanded)}
          title={expanded ? "Show less" : "Read more"}
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </div>
  );
};

// View Details Modal Component
const SellerDetailsModal = ({ seller, onClose, onAction, onVerifySection, sectionVerifying }) => {
  if (!seller) return null;

  // Build pickup address for modal
  const getFullPickupAddress = () => {
    const parts = [];
    if (seller.pickupHouseNo) parts.push(seller.pickupHouseNo);
    if (seller.pickupAddress) parts.push(seller.pickupAddress);
    if (seller.pickupCity) parts.push(seller.pickupCity);
    if (seller.pickupState) parts.push(seller.pickupState);
    if (seller.pickupPincode) parts.push(seller.pickupPincode);
    return parts.length > 0 ? parts.join(', ') : '-';
  };

  const getModalActions = () => {
    switch (seller.status) {
      case "pending":
        return [
          { label: "Approve", onClick: () => onAction("approve"), variant: "approve", icon: "✓" },
          { label: "Reject", onClick: () => onAction("reject"), variant: "reject", icon: "✗" },
        ];
      case "approved":
        return [
          { label: "Revert to Pending", onClick: () => onAction("revert"), variant: "revert", icon: "↩️" },
        ];
      case "rejected":
        return [
          { label: "Revert to Pending", onClick: () => onAction("revert"), variant: "revert", icon: "↩️" },
        ];
      default:
        return [];
    }
  };

  const renderGroupHeader = (title, sectionKey, currentStatus) => {
    return (
      <div className="group-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #e5e7eb', paddingBottom: '8px' }}>
        <h4 style={{ margin: 0, border: 'none', padding: 0 }}>{title}</h4>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span className={`verification-badge ${currentStatus || 'pending'}`} style={{
            fontSize: '10px',
            fontWeight: '600',
            padding: '2px 8px',
            borderRadius: '12px',
            textTransform: 'uppercase',
            backgroundColor: currentStatus === 'verified' ? '#e8f5e9' : currentStatus === 'rejected' ? '#ffebee' : '#fff3e0',
            color: currentStatus === 'verified' ? '#2e7d32' : currentStatus === 'rejected' ? '#c62828' : '#e65100',
            border: `1px solid ${currentStatus === 'verified' ? '#c8e6c9' : currentStatus === 'rejected' ? '#ffcdd2' : '#ffe0b2'}`
          }}>
            {currentStatus === 'verified' ? 'Verified' : currentStatus === 'rejected' ? 'Needs Update' : 'Pending'}
          </span>
          <button 
            onClick={() => onVerifySection(seller.customId, sectionKey, true)} 
            className="verify-section-btn verify"
            disabled={sectionVerifying}
            style={{
              padding: '3px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              transition: 'opacity 0.2s'
            }}
          >
            Verify
          </button>
          <button 
            onClick={() => onVerifySection(seller.customId, sectionKey, false)} 
            className="verify-section-btn not-synced"
            disabled={sectionVerifying}
            style={{
              padding: '3px 8px',
              borderRadius: '4px',
              fontSize: '11px',
              fontWeight: '600',
              cursor: 'pointer',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              transition: 'opacity 0.2s'
            }}
          >
            Not Synced
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <h3>Seller Details</h3>
            <span className={`modal-status status-${seller.status}`}>
              {seller.status.toUpperCase()}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="details-grid">
            <div className="detail-group">
              {renderGroupHeader("Basic Information", "basic", seller.basicInfoVerified)}
              <div className="detail-item">
                <span className="detail-label">Seller ID:</span>
                <span className="detail-value">{seller.customId}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Firebase UID:</span>
                <div className="uid-container">
                  <span className="detail-value uid-value">{seller.firebaseUid || "-"}</span>
                  {seller.firebaseUid && (
                    <button
                      className="copy-btn"
                      onClick={() => {
                        navigator.clipboard.writeText(seller.firebaseUid);
                        alert("Firebase UID copied to clipboard!");
                      }}
                      title="Copy to clipboard"
                    >
                      📋
                    </button>
                  )}
                </div>
              </div>
              <div className="detail-item">
                <span className="detail-label">Name:</span>
                <span className="detail-value">{seller.name || "-"}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Email:</span>
                <span className="detail-value">{seller.email}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Phone:</span>
                <span className="detail-value">{seller.phoneNumber}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Created At:</span>
                <span className="detail-value">{seller.createdAt ? new Date(seller.createdAt).toLocaleString() : "-"}</span>
              </div>
            </div>

            <div className="detail-group">
              {renderGroupHeader("Company Details", "company", seller.companyDetailsVerified)}
              <div className="detail-item">
                <span className="detail-label">Company Name:</span>
                <span className="detail-value">{seller.companyName || "-"}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Company PAN:</span>
                <div className="uid-container">
                  <span className="detail-value uid-value">{seller.companyPan || "-"}</span>
                  {seller.panCardUrl && (
                    <a href={seller.panCardUrl} target="_blank" rel="noopener noreferrer" className="copy-btn" title="View PAN Document" style={{ textDecoration: 'none' }}>
                      📄
                    </a>
                  )}
                </div>
              </div>
              <div className="detail-item">
                <span className="detail-label">GST Number:</span>
                <div className="uid-container">
                  <span className="detail-value uid-value">{seller.gstNumber || "-"}</span>
                  {seller.gstCertUrl && (
                    <a href={seller.gstCertUrl} target="_blank" rel="noopener noreferrer" className="copy-btn" title="View GST Certificate" style={{ textDecoration: 'none' }}>
                      📄
                    </a>
                  )}
                </div>
              </div>
              <div className="detail-item">
                <span className="detail-label">Authorized Person:</span>
                <span className="detail-value">{seller.authorized || "-"}</span>
              </div>
            </div>

            <div className="detail-group">
              {renderGroupHeader("Bank Details", "bank", seller.bankDetailsVerified)}
              <div className="detail-item">
                <span className="detail-label">Bank Name:</span>
                <span className="detail-value">{seller.bankName || "-"}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Account Number:</span>
                <div className="uid-container">
                  <span className="detail-value uid-value">{seller.bankAccountNumber || "-"}</span>
                  {seller.bankPassbookUrl && (
                    <a href={seller.bankPassbookUrl} target="_blank" rel="noopener noreferrer" className="copy-btn" title="View Bank Passbook" style={{ textDecoration: 'none' }}>
                      📄
                    </a>
                  )}
                </div>
              </div>
              <div className="detail-item">
                <span className="detail-label">IFSC Code:</span>
                <span className="detail-value">{seller.bankIFCnumber || "-"}</span>
              </div>
            </div>

            <div className="detail-group">
              {renderGroupHeader("🚚 Shiprocket Details", "shiprocket", seller.shiprocketDetailsVerified)}
              <div className="detail-item full-width">
                <span className="detail-label">Pickup Address:</span>
                <span className="detail-value address-full">
                  {getFullPickupAddress()}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Shiprocket Nickname:</span>
                <span className="detail-value">{seller.shiprocketNickname || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Sync Status:</span>
                <span className="detail-value">
                  {seller.shiprocketSyncSuccess ? (
                    <span className="status-badge status-success">✅ Synced</span>
                  ) : seller.shiprocketSyncAttempts > 0 ? (
                    <span className="status-badge status-error">❌ Failed</span>
                  ) : (
                    <span className="status-badge status-pending">⏳ Not Synced</span>
                  )}
                </span>
              </div>
              {seller.shiprocketSyncSuccess && (
                <>
                  <div className="detail-item">
                    <span className="detail-label">Pickup ID:</span>
                    <div className="uid-container">
                      <span className="detail-value uid-value">{seller.shiprocketPickupId || '-'}</span>
                      {seller.shiprocketPickupId && (
                        <button
                          className="copy-btn"
                          onClick={() => {
                            navigator.clipboard.writeText(seller.shiprocketPickupId);
                            alert("Pickup ID copied to clipboard!");
                          }}
                          title="Copy to clipboard"
                        >
                          📋
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Verification:</span>
                    <span className="detail-value">
                      {seller.shiprocketVerificationStatus === 'verified' ? (
                        <span className="status-badge status-success">✅ Verified</span>
                      ) : (
                        <span className="status-badge status-pending">⏳ Pending Verification</span>
                      )}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Synced At:</span>
                    <span className="detail-value">
                      {seller.shiprocketSyncCompletedAt ? 
                        new Date(seller.shiprocketSyncCompletedAt).toLocaleString() : '-'}
                    </span>
                  </div>
                  {seller.shiprocketVerifiedAt && (
                    <div className="detail-item">
                      <span className="detail-label">Verified At:</span>
                      <span className="detail-value">
                        {new Date(seller.shiprocketVerifiedAt).toLocaleString()}
                      </span>
                    </div>
                  )}
                </>
              )}
              {seller.shiprocketSyncError && (
                <div className="detail-item error">
                  <span className="detail-label">Last Error:</span>
                  <span className="detail-value error-text">
                    {seller.shiprocketSyncError.message}
                  </span>
                </div>
              )}
            </div>

            <div className="detail-group">
              {renderGroupHeader("💰 Razorpay Details", "razorpay", seller.razorpayVerified ? "verified" : seller.razorpayStatus === "failed" ? "rejected" : "pending")}
              <div className="detail-item">
                <span className="detail-label">Contact ID:</span>
                <span className="detail-value">{seller.razorpayContactId || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Fund Account ID:</span>
                <span className="detail-value">{seller.razorpayFundAccountId || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Validation ID:</span>
                <span className="detail-value">{seller.razorpayValidationId || '-'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Verification Status:</span>
                <span className="detail-value">
                  {seller.razorpayVerified ? (
                    <span className="status-badge status-success">✅ Verified</span>
                  ) : (
                    <span className="status-badge status-pending">⏳ Pending</span>
                  )}
                </span>
              </div>
              {seller.razorpayVerifiedAt && (
                <div className="detail-item">
                  <span className="detail-label">Verified At:</span>
                  <span className="detail-value">
                    {new Date(seller.razorpayVerifiedAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div className="modal-actions">
            {getModalActions().map((action, index) => (
              <button
                key={index}
                onClick={action.onClick}
                className={`modal-btn action-${action.variant}`}
              >
                <span className="action-icon">{action.icon}</span>
                {action.label}
              </button>
            ))}
            <button
              className="modal-btn delete-btn"
              onClick={() => {
                if (window.confirm("Are you sure you want to delete this seller?")) {
                  onAction("delete");
                }
              }}
            >
              <span className="action-icon">🗑️</span>
              Delete
            </button>
          </div>
          <button className="modal-btn secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default function SellerApprovalPanel() {
  const [sellers, setSellers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("pending");
  const [error, setError] = useState("");
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [verifyingSellerId, setVerifyingSellerId] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [fetchSellers, { loading }] = useLazyQuery(GET_SELLERS_QUERY, {
    fetchPolicy: "no-cache",
    onCompleted: (data) => {
      const sellersData = data?.getSellers ?? [];
      setSellers(sellersData);
      setError("");
      setCurrentPage(1);
    },
    onError: (err) => {
      setError(err.message);
    },
  });

  const [updateStatusMutation, { loading: actionLoading }] = useMutation(UPDATE_SELLER_STATUS_MUTATION, {
    onCompleted: () => {
      fetchSellers();
      setShowDetailsModal(false);
      setSelectedSeller(null);
    },
    onError: (err) => {
      alert(err.message || "Status update failed");
    },
  });

  const [deleteSellerMutation] = useMutation(DELETE_SELLER_MUTATION, {
    onCompleted: () => {
      fetchSellers();
      setShowDetailsModal(false);
      setSelectedSeller(null);
    },
    onError: (err) => {
      alert(err.message);
    },
  });

  const [verifyShiprocketMutation] = useMutation(VERIFY_SHIPROCKET_MUTATION, {
    onCompleted: (data) => {
      alert("Shiprocket seller verified successfully!");
      fetchSellers();
      setVerifyingSellerId(null);
    },
    onError: (err) => {
      alert("Failed to verify seller: " + err.message);
      setVerifyingSellerId(null);
    },
  });

  const [verifyProfileSectionMutation, { loading: sectionVerifying }] = useMutation(VERIFY_PROFILE_SECTION_MUTATION, {
    onCompleted: (data) => {
      if (selectedSeller && data?.verifyProfileSection) {
        setSelectedSeller(prev => {
          if (!prev) return null;
          return {
            ...prev,
            ...data.verifyProfileSection
          };
        });
      }
      fetchSellers();
      alert("Section verification status updated!");
    },
    onError: (err) => {
      alert("Failed to update section verification: " + err.message);
    }
  });

  const handleVerifySection = async (customId, section, verified) => {
    let remarks = null;
    if (!verified) {
      remarks = window.prompt(`Enter remarks/reason why ${section} details require updates:`);
      if (remarks === null) return;
    }
    await verifyProfileSectionMutation({
      variables: {
        customId,
        section,
        verified,
        remarks: remarks || "",
      },
    });
  };

  useEffect(() => {
    fetchSellers();
  }, [fetchSellers]);

  const updateStatus = async (customId, statusEnum, reason) => {
    await updateStatusMutation({
      variables: {
        customId,
        status: statusEnum,
        reason: reason || null,
      },
    });
  };

  const handleRejectStatus = (customId) => {
    const reason = window.prompt("Enter verification update details / rejection remarks:");
    if (reason === null) return; // User cancelled
    updateStatus(customId, "rejected", reason);
  };

  const deleteSeller = async (customId) => {
    if (!window.confirm("Are you sure you want to delete this seller permanently?")) return;
    await deleteSellerMutation({
      variables: { customId },
    });
  };

  const verifyShiprocket = async (customId) => {
    setVerifyingSellerId(customId);
    await verifyShiprocketMutation({
      variables: { customId },
    });
  };

  const handleViewDetails = (seller) => {
    setSelectedSeller(seller);
    setShowDetailsModal(true);
  };

  const handleModalAction = (actionType) => {
    if (!selectedSeller) return;

    switch (actionType) {
      case "approve":
        updateStatus(selectedSeller.customId, "approved");
        break;
      case "reject":
        handleRejectStatus(selectedSeller.customId);
        break;
      case "revert":
        updateStatus(selectedSeller.customId, "pending");
        break;
      case "delete":
        deleteSeller(selectedSeller.customId);
        break;
      default:
        break;
    }
  };

  // Filter sellers by status and search
  const filteredSellers = sellers.filter(seller => {
    if (seller.status !== activeTab) return false;

    if (!searchTerm) return true;

    const term = searchTerm.toLowerCase();
    return (
      seller.customId.toLowerCase().includes(term) ||
      (seller.name && seller.name.toLowerCase().includes(term)) ||
      seller.companyName.toLowerCase().includes(term) ||
      seller.email.toLowerCase().includes(term) ||
      seller.phoneNumber.toLowerCase().includes(term) ||
      (seller.firebaseUid && seller.firebaseUid.toLowerCase().includes(term))
    );
  });

  // Pagination calculations
  const totalItems = filteredSellers.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Calculate items to show on current page
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSellers = filteredSellers.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getActionButtons = (seller) => {
    const actions = {
      pending: [
        { onClick: () => updateStatus(seller.customId, "approved"), variant: "approve", icon: "✓", title: "Approve" },
        { onClick: () => handleRejectStatus(seller.customId), variant: "reject", icon: "✗", title: "Reject" },
      ],
      approved: [
        { onClick: () => updateStatus(seller.customId, "pending"), variant: "revert", icon: "↩️", title: "Revert to Pending" },
      ],
      rejected: [
        { onClick: () => updateStatus(seller.customId, "pending"), variant: "revert", icon: "↩️", title: "Revert to Pending" },
      ]
    };

    return (
      <div className="btn-group">
        <button
          onClick={() => handleViewDetails(seller)}
          className="action-btn action-view"
          title="View Details"
        >
          <span className="action-icon">👁️</span>
        </button>
        {actions[activeTab]?.map((action, index) => (
          <button
            key={index}
            onClick={action.onClick}
            className={`action-btn action-${action.variant}`}
            disabled={actionLoading}
            title={action.title}
          >
            <span className="action-icon">{action.icon}</span>
          </button>
        ))}
        <button
          onClick={() => deleteSeller(seller.customId)}
          className="action-btn action-delete"
          title="Delete Seller"
          disabled={actionLoading}
        >
          <span className="action-icon">🗑️</span>
        </button>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

 // Update the renderTable function with proper column widths and structure
const renderTable = () => {
  const statusLabel = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
  const countByStatus = sellers.filter(s => s.status === activeTab).length;

  return (
    <section className="table-section">
      <div className="section-header">
        <h2>
          {statusLabel} Sellers
          <span className="count-badge">{totalItems}</span>
          {countByStatus !== totalItems && (
            <span className="filtered-badge">
              (Filtered from {countByStatus})
            </span>
          )}
        </h2>
      </div>

      <div className="table-scroll-container">
        {/* Fixed Custom ID Column */}
        <div className="fixed-column" style={{ minWidth: '180px', width: '180px' }}>
          <table className="fixed-table" style={{ width: '100%' }}>
            <thead>
              <tr style={{ height: '48px' }}>
                <th className="fixed-header" style={{ width: '180px' }}>Seller ID</th>
              </tr>
            </thead>
            <tbody>
              {currentSellers.length === 0 ? (
                <tr>
                  <td className="empty-cell fixed-cell" style={{ width: '180px', height: '68px' }}>
                    {searchTerm
                      ? `No matching ${activeTab} sellers found`
                      : `No ${activeTab} sellers available`
                    }
                  </td>
                </tr>
              ) : (
                currentSellers.map((seller) => (
                  <tr key={seller.customId}>
                    <td className="fixed-cell" style={{ width: '180px', height: '68px' }}>
                      <div className="seller-id">
                        <strong>{seller.customId}</strong>
                        <small>{formatDate(seller.createdAt)}</small>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Scrollable Content */}
        <div className="scrollable-content" style={{ flex: 1, overflowX: 'auto' }}>
          <table className="scrollable-table" style={{ minWidth: '1200px', width: '100%' }}>
            <thead>
              <tr style={{ height: '48px' }}>
                <th style={{ minWidth: '140px', width: '140px' }}>Name</th>
                <th style={{ minWidth: '130px', width: '130px' }}>Company</th>
                <th style={{ minWidth: '100px', width: '100px' }}>Contact</th>
                <th style={{ minWidth: '180px', width: '180px' }}>Firebase UID</th>
                <th style={{ minWidth: '90px', width: '90px' }}>PAN</th>
                <th style={{ minWidth: '120px', width: '120px' }}>GST</th>
                <th style={{ minWidth: '200px', width: '200px' }}>Pickup Address</th>
                <th style={{ minWidth: '150px', width: '150px' }}>Bank Details</th>
                <th style={{ minWidth: '100px', width: '100px' }}>Status</th>
                <th style={{ minWidth: '180px', width: '180px' }}>Shiprocket</th>
                <th style={{ minWidth: '140px', width: '140px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentSellers.length === 0 ? (
                <tr>
                  <td colSpan="11" className="empty-cell" style={{ height: '68px' }}>
                    {searchTerm ? (
                      <div className="empty-state">
                        <div className="empty-icon">🔍</div>
                        <div>No matching sellers found</div>
                        <button
                          onClick={() => setSearchTerm("")}
                          className="clear-search-btn"
                        >
                          Clear Search
                        </button>
                      </div>
                    ) : (
                      <div className="empty-state">
                        <div className="empty-icon">📭</div>
                        <div>No {activeTab} sellers available</div>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                currentSellers.map((seller) => (
                  <tr key={seller.customId} style={{ height: '68px' }}>
                    <td style={{ width: '140px' }}>
                      <div className="seller-info">
                        <strong>{seller.name || "-"}</strong>
                        <small className="email">{seller.email}</small>
                      </div>
                    </td>
                    <td style={{ width: '130px' }}>
                      <div className="company-info">
                        <strong>{seller.companyName || "-"}</strong>
                        <small>{seller.companyPan || "No PAN"}</small>
                      </div>
                    </td>
                    <td style={{ width: '100px' }}>
                      <div className="contact-info">
                        <div className="phone">{seller.phoneNumber}</div>
                      </div>
                    </td>
                    <td style={{ width: '180px' }}>
                      <div className="firebase-uid">
                        <div className="uid-text" title={seller.firebaseUid}>
                          {seller.firebaseUid ? `${seller.firebaseUid.substring(0, 15)}...` : "-"}
                        </div>
                        {seller.firebaseUid && (
                          <button
                            className="copy-uid-btn"
                            onClick={() => {
                              navigator.clipboard.writeText(seller.firebaseUid);
                              alert("Firebase UID copied to clipboard!");
                            }}
                            title="Copy to clipboard"
                          >
                            📋
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ width: '90px' }}>{seller.companyPan || "-"}</td>
                    <td style={{ width: '120px' }}>{seller.gstNumber || "-"}</td>
                    <td style={{ width: '200px' }}>
                      <AddressCell seller={seller} />
                    </td>
                    <td style={{ width: '150px' }}>
                      <div className="bank-info">
                        <div>{seller.bankName || "-"}</div>
                        <small>Acc: {seller.bankAccountNumber ? `${seller.bankAccountNumber.substring(0, 10)}...` : "-"}</small>
                      </div>
                    </td>
                    <td style={{ width: '100px' }}>
                      <span className={`status-badge status-${seller.status}`}>
                        {seller.status}
                      </span>
                    </td>
                    <td style={{ width: '180px' }}>
                      <ShiprocketStatusBadge 
                        seller={seller} 
                        onVerify={activeTab === "approved" ? verifyShiprocket : null}
                        isActionLoading={verifyingSellerId === seller.customId}
                      />
                    </td>
                    <td style={{ width: '140px' }}>
                      {getActionButtons(seller)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="table-footer">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
          />
        </div>
      )}
    </section>
  );
};

  if (loading && sellers.length === 0) {
    return (
      <div className="seller-panel">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading sellers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="seller-panel">
      <header className="panel-header">
        <h1>Seller Approval Panel</h1>
        <p className="panel-subtitle">
          Manage seller registrations, approvals, and verifications
        </p>
      </header>

      <div className="tabs-container">
        <Tab
          active={activeTab === "pending"}
          label="⏳ Pending"
          count={sellers.filter(s => s.status === "pending").length}
          onClick={() => {
            setActiveTab("pending");
            setCurrentPage(1);
          }}
        />
        <Tab
          active={activeTab === "approved"}
          label="✅ Approved"
          count={sellers.filter(s => s.status === "approved").length}
          onClick={() => {
            setActiveTab("approved");
            setCurrentPage(1);
          }}
        />
        <Tab
          active={activeTab === "rejected"}
          label="❌ Rejected"
          count={sellers.filter(s => s.status === "rejected").length}
          onClick={() => {
            setActiveTab("rejected");
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="controls-container">
        <div className="search-controls">
          <div className="search-wrapper">
            <input
              type="text"
              placeholder={`Search ${activeTab} sellers...`}
              className="search-input"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
            />
            {searchTerm && (
              <button
                className="clear-search-btn"
                onClick={() => {
                  setSearchTerm("");
                  setCurrentPage(1);
                }}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <button
            onClick={() => fetchSellers()}
            className="refresh-btn"
            disabled={loading}
          >
            {loading ? "🔄" : "🔄 Refresh"}
          </button>
        </div>

        <div className="stats-container">
          <div className="stat">
            <span className="stat-label">Total:</span>
            <span className="stat-value">{sellers.length}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Showing:</span>
            <span className="stat-value">{totalItems}</span>
          </div>
          <div className="stat">
            <span className="stat-label">Page:</span>
            <span className="stat-value">{currentPage} of {totalPages || 1}</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button
            onClick={() => fetchSellers()}
            className="retry-btn"
          >
            Retry
          </button>
        </div>
      )}

      {(actionLoading || verifyingSellerId) && (
        <div className="action-loading">
          <div className="loading-spinner small"></div>
          Processing action...
        </div>
      )}

      {renderTable()}

      {showDetailsModal && (
        <SellerDetailsModal
          seller={selectedSeller}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedSeller(null);
          }}
          onAction={handleModalAction}
          onVerifySection={handleVerifySection}
          sectionVerifying={sectionVerifying}
        />
      )}
    </div>
  );
};